/**
 * VSC reports management page.
 * List with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { VscReportsDataTable } from '@/components/vsc-reports/VscReportsDataTable';
import { CreateVscReportView } from '@/components/vsc-reports/CreateVscReportView';
import { EditVscReportView } from '@/components/vsc-reports/EditVscReportView';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { DEFAULT_VSC_REPORT_FILTERS } from '@/components/vsc-reports/vsc-report-configs';
import { usePageActions } from '@/hooks/usePageActions';
import { Button } from '@/components/forms/Button';
import { Spinner } from '@/components/ui/spinner';
import { getVscReport } from '@/services/vsc-reports.service';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** VSC reports list; a `?view=` deep-link pins a single report until cleared. */
export const VscReportsManagementPage: React.FC = () => {
  const { canCreate } = usePageActions('vscReports');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editReportId, setEditReportId] = React.useState<string | null>(null);
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('vsc-reports-management');
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [deepLinkReportId, setDeepLinkReportId] = React.useState<string | null>(null);

  /** Deep-link: open create/view via URL params (also strips leftover filter params). */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (viewId) {
      setOpenRow(viewId);
      setDeepLinkReportId(viewId);
      setActiveForm(null);
      setEditReportId(null);
    }

    if (shouldCreate) {
      setActiveForm('create');
      setEditReportId(null);
      closeRow();
    }

    if (
      viewId ||
      searchParams.has('reportDate') ||
      searchParams.has('establishmentId') ||
      shouldCreate
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('view');
          next.delete('reportDate');
          next.delete('establishmentId');
          next.delete('create');
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, closeRow, setOpenRow, setSearchParams]);

  const {
    data: deepLinkRaw,
    isLoading: isLoadingDeepLink,
    isError: isDeepLinkError,
    error: deepLinkError,
  } = useQuery({
    queryKey: ['vsc-report', 'deep-link', deepLinkReportId],
    queryFn: () => getVscReport(deepLinkReportId!),
    enabled: !!deepLinkReportId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isDeepLinkError,
    error: deepLinkError,
    catalogKey: 'vscReportNotFound',
    onClose: closeRow,
    entityId: deepLinkReportId,
  });

  /** One-row list wrapping the fetched `?view=` report (empty until it loads). */
  const deepLinkRows = React.useMemo(() => (deepLinkRaw ? [deepLinkRaw] : []), [deepLinkRaw]);

  /** Drop the pinned deep-link report when the user closes the details row. */
  const prevOpenRowIdRef = React.useRef(openRowId);
  React.useEffect(() => {
    if (prevOpenRowIdRef.current !== null && openRowId === null) {
      setDeepLinkReportId(null);
    }
    prevOpenRowIdRef.current = openRowId;
  }, [openRowId]);

  /** Return to the full list after a notification deep-link. */
  const clearDeepLink = React.useCallback(() => {
    setDeepLinkReportId(null);
    closeRow();
  }, [closeRow]);

  /** Refresh VSC-report queries then close the form (await so the table sees new data). */
  const closeForm = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
    ]);
    setActiveForm(null);
    setEditReportId(null);
  }, [queryClient]);

  /** Swap the table for the edit form and close any open details row. */
  const handleEditReport = React.useCallback(
    (reportId: string) => {
      setEditReportId(reportId);
      setActiveForm('edit');
      closeRow();
    },
    [closeRow],
  );

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'create') {
      return <CreateVscReportView onClose={closeForm} onSuccess={closeForm} />;
    }
    if (activeForm === 'edit' && editReportId) {
      return (
        <EditVscReportView key={editReportId} vscReportId={editReportId} onClose={closeForm} />
      );
    }
    return null;
  }, [activeForm, editReportId, closeForm]);

  return (
    <ManagementPage
      title="Gestion des Rapports VSC"
      subtitle="Créez, modifiez et gérez tous vos rapports de suivi VSC"
      contentRef={contentRef}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setActiveForm('create');
              setEditReportId(null);
              closeRow();
            }}
          >
            Créer un rapport VSC
          </Button>
        ) : undefined
      }
    >
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        {deepLinkReportId ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm">
              <span>Vous consultez un rapport spécifique (ouvert depuis une notification).</span>
              <Button variant="ghost" size="sm" onClick={clearDeepLink}>
                <X className="mr-1 h-4 w-4" /> Voir tous les rapports
              </Button>
            </div>
            {isLoadingDeepLink && deepLinkRows.length === 0 ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : (
              <VscReportsDataTable
                vscReports={deepLinkRows}
                expandedRowId={openRowId}
                onToggleRow={openRow}
                onCloseDetails={closeRow}
                onEditReport={handleEditReport}
                enableFilters={false}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            )}
          </div>
        ) : (
          <VscReportsDataTable
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            onEditReport={handleEditReport}
            enableFilters
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            defaultFilters={DEFAULT_VSC_REPORT_FILTERS}
          />
        )}
      </CollapseReplaceWrapper>
    </ManagementPage>
  );
};
